type Nullable = string | null | undefined;

export function normalizeBase(value: Nullable): string {
  if (value == null) return "";
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function normalizeUnit(value: Nullable): string {
  let s = normalizeBase(value);
  if (s === "") return "";

  // Treat the literal serialization artifacts "null"/"undefined" as absent.
  // CookUnity began sending apartment:"null" (a literal string, not JSON null)
  // on 2026-06-14, which otherwise compares as a real unit and produces spurious
  // unit-number mismatches against an empty/genuinely-null actual. No real unit
  // is ever named "null" or "undefined", so this is safe as a global guard and
  // it also neutralizes any already-stored "null" baselines without a backfill.
  if (s === "null" || s === "undefined") return "";

  // Strip parenthetical content — e.g. "Unit B (upper)" → "Unit B"
  s = s.replace(/\s*\([^)]*\)/g, "").trim();

  // Strip content after "/" separator — e.g. "Suite 1509 / Buzzer 1439" → "Suite 1509"
  s = s.replace(/\s*\/.*$/, "").trim();

  // Strip trailing entry-system instruction keywords and everything after them.
  // Covers: "2303 buzz 243" → "2303", "617 dial 1200 on intercom" → "617",
  //         "2411 AND BUZZER 3611" → "2411"
  s = s.replace(/\s+(?:and\s+)?(?:buzz(?:er)?|dial|intercom)(?:\s+.*)?$/i, "").trim();

  // Handle hyphenated building-unit format — e.g. "11-1703" → "1703".
  // In Canadian multi-unit addressing, NN-NNNN means building NN, unit NNNN.
  const hyphenMatch = s.match(/^\d+-(\d+[a-z]?)$/i);
  if (hyphenMatch) return hyphenMatch[1];

  // If "unit" appears after other content, take what follows it.
  // e.g. "Carver 2 Unit 26" → "26", "LPH unit 2407" → "2407"
  // The `.+` guard ensures we only fire when there is content before "unit".
  if (/^.+\bunit\s+/i.test(s)) {
    s = s.replace(/^.*\bunit\s+/i, "").trim();
  }

  // Strip leading prefix keywords.
  // Note: ^# is applied last so it also catches # left behind after another prefix
  // is stripped (e.g. "Unit #1003" → strip "unit " → "#1003" → strip "#" → "1003").
  s = s
    .replace(/^apt\.?\s*/i, "")
    .replace(/^apartment\s*/i, "")
    .replace(/^unit\.?\s*(?:no\.?\s*)?/i, "") // handles "unit", "unit.", "unit no."
    .replace(/^ste\.?\s*/i, "")
    .replace(/^suite\s*/i, "")
    .replace(/^fl(?:oor)?\.?\s*/i, "")
    .replace(/^rm\.?\s*/i, "")
    .replace(/^room\s*/i, "")
    .replace(/^townhous(?:e)?\s*/i, "")        // townhouse, townhse
    .replace(/^townhome\s*/i, "")
    .replace(/^#\s*/, "")                       // catches bare # and # left after prefix strip
    .trim();

  return s;
}

const DROP_ZONE_CANONICAL: Record<string, string> = {
  "front door": "front door",
  "front": "front door",
  "fd": "front door",

  "side door": "side door",
  "side": "side door",
  "sd": "side door",

  "basement door": "basement door",
  "basement": "basement door",

  "interior location": "interior location",
  "interior": "interior location",
  "inside": "interior location",

  "other location": "other location",
  "other": "other location",

  "h2h": "h2h",
  "hand to hand": "h2h",
  "hand-to-hand": "h2h",
  "handed": "h2h",

  "unit door": "unit door",
  "unit": "unit door",

  "concierge": "concierge",
  "doorman": "concierge",

  "lobby": "lobby",

  "package area": "package area",
  "package room": "package area",
  "mailroom": "package area",
  "mail room": "package area",
  "pkg area": "package area",

  "loading dock": "loading dock",
  "dock": "loading dock",
  "receiving": "loading dock",

  "department": "department",
  "dept": "department",

  "counter": "counter",

  "on top": "on top",
  "on-top": "on top",
  "ontop": "on top",

  "hidden": "hidden",

  "next to": "next to",
  "next-to": "next to",
  "beside": "next to",

  "beneath": "beneath",
  "under": "beneath",
  "underneath": "beneath",
};

export function normalizeDropZone(value: Nullable): string {
  const base = normalizeBase(value);
  if (base === "") return "";
  return DROP_ZONE_CANONICAL[base] ?? base;
}

export function normalizeCompletionRequirements(value: Nullable | string[]): Set<string> {
  if (value == null) return new Set();
  const items = Array.isArray(value) ? value : value.split(",");
  const set = new Set<string>();
  for (const item of items) {
    const normalized = normalizeBase(item);
    if (normalized !== "") set.add(normalized);
  }
  return set;
}

export function completionRequirementsMatch(
  a: Nullable | string[],
  b: Nullable | string[],
): boolean {
  const setA = normalizeCompletionRequirements(a);
  const setB = normalizeCompletionRequirements(b);
  if (setA.size !== setB.size) return false;
  const arrA = Array.from(setA);
  for (let i = 0; i < arrA.length; i++) {
    if (!setB.has(arrA[i])) return false;
  }
  return true;
}

export type FieldType = "text" | "unit" | "dropZone" | "completionRequirements";

export function normalizeField(value: Nullable | string[], fieldType: FieldType): string {
  switch (fieldType) {
    case "unit":
      return normalizeUnit(value as Nullable);
    case "dropZone":
      return normalizeDropZone(value as Nullable);
    case "completionRequirements": {
      const set = normalizeCompletionRequirements(value);
      return Array.from(set).sort().join(",");
    }
    case "text":
    default:
      return normalizeBase(value as Nullable);
  }
}

export function fieldsMatch(
  a: Nullable | string[],
  b: Nullable | string[],
  fieldType: FieldType,
): boolean {
  if (fieldType === "completionRequirements") {
    return completionRequirementsMatch(a, b);
  }
  const na = normalizeField(a, fieldType);
  const nb = normalizeField(b, fieldType);
  if (na === "" && nb === "") return true;
  if (na === "" || nb === "") return false;
  return na === nb;
}
